import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios, { API } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";

const CATEGORIES = ["Semua", "Haji", "Umrah", "Manasik", "Tips Persiapan", "Doa", "Berita KBIHU"];

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
}

export default function Artikel() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Semua");
  const navigate = useNavigate();

  useEffect(() => {
    const params = { limit: 12 };
    if (category !== "Semua") params.category = category;
    if (search) params.search = search;
    axios.get(`${API}/public/articles`, { params }).then(({ data }) => setData(data)).catch(() => setData({ items: [] }));
  }, [category, search]);

  return (
    <div data-testid="artikel-page">
      <section className="bg-gradient-to-br from-[#084C3A] to-[#0B4F71] px-4 py-16 text-center sm:px-6">
        <h1 className="font-heading text-3xl font-bold text-white sm:text-4xl">Artikel &amp; Wawasan</h1>
        <p className="mt-3 text-white/80">Bacaan seputar Haji, Umrah, manasik, dan tips persiapan ibadah.</p>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center">
          <Tabs value={category} onValueChange={setCategory}>
            <TabsList data-testid="article-category-tabs" className="flex-wrap justify-start gap-1 rounded-full bg-[#F5F9F7] h-auto p-1.5">
              {CATEGORIES.map((c) => (
                <TabsTrigger key={c} value={c} data-testid={`article-tab-${c.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-full">{c}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748B]" />
            <Input data-testid="article-search-input" placeholder="Cari artikel..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-11 rounded-full pl-9" />
          </div>
        </div>

        {data === null ? (
          <LoadingScreen />
        ) : data.items.length === 0 ? (
          <div className="mt-10"><EmptyState title="Artikel tidak ditemukan" description="Coba ubah kata kunci atau kategori pencarian." /></div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((a) => (
              <button key={a.id} data-testid={`article-list-card-${a.id}`} onClick={() => navigate(`/artikel/${a.slug}`)} className="overflow-hidden rounded-2xl bg-white text-left shadow-[0_2px_16px_rgba(15,107,79,0.06)] transition-all duration-300 hover:-translate-y-1">
                <img src={a.cover_image} alt={a.title} className="h-44 w-full object-cover" />
                <div className="p-5">
                  <Badge variant="outline" className="rounded-full border-[#0F6B4F]/30 text-[#0F6B4F]">{a.category}</Badge>
                  <h3 className="mt-3 font-heading text-base font-semibold text-[#1F2937] line-clamp-2">{a.title}</h3>
                  <p className="mt-2 text-sm text-[#64748B] line-clamp-2">{a.excerpt}</p>
                  <div className="mt-4 flex items-center gap-3 text-xs text-[#64748B]">
                    <span>{a.author}</span> &middot; <span>{formatDate(a.published_at)}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {a.reading_time} menit</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
